
import { supabase } from "@/integrations/supabase/client";
import { Message } from "../message-item";

/**
 * Service for managing conversation messages in the database
 */
export class ConversationMessageService {
  
  /**
   * Save messages to the database for a conversation
   */
  static async saveMessages(threadId: string, messages: Message[]): Promise<boolean> {
    try {
      console.log(`💾 [MESSAGE-SERVICE] ===== STARTING MESSAGE SAVE =====`);
      console.log(`💾 [MESSAGE-SERVICE] Thread ID: ${threadId}`);
      console.log(`💾 [MESSAGE-SERVICE] Messages count: ${messages.length}`);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("❌ [MESSAGE-SERVICE] User error:", userError);
        return false;
      }
      
      if (!user) {
        console.error("❌ [MESSAGE-SERVICE] No user found");
        return false;
      }

      console.log(`💾 [MESSAGE-SERVICE] User authenticated: ${user.id}`);

      // Get conversation ID from thread_id - this might create the conversation if it doesn't exist
      let { data: conversation, error: convError } = await supabase
        .from('chat_conversations')
        .select('id')
        .eq('thread_id', threadId)
        .eq('user_id', user.id)
        .single();

      if (convError || !conversation) {
        console.log('⚠️ [MESSAGE-SERVICE] No conversation found for thread:', threadId);
        console.log('⚠️ [MESSAGE-SERVICE] Conversation error:', convError);
        
        // Try to find any conversations for this user to debug
        const { data: allConversations, error: allConvError } = await supabase
          .from('chat_conversations')
          .select('id, thread_id, title')
          .eq('user_id', user.id);
        
        console.log('🔍 [MESSAGE-SERVICE] All conversations for user:', allConversations);
        console.log('🔍 [MESSAGE-SERVICE] All conversations error:', allConvError);
        
        // This is expected for new conversations that haven't been saved yet
        return true; 
      }

      console.log(`💾 [MESSAGE-SERVICE] Found conversation ID: ${conversation.id}`);

      // Filter out welcome messages and prepare messages for database
      // IMPORTANT: Deduplicate messages by message_id to prevent database conflicts
      const uniqueMessages = new Map();
      
      messages
        .filter(msg => !msg.isWelcomeMessage)
        .forEach(msg => {
          // Use message_id as key to ensure uniqueness
          uniqueMessages.set(msg.id, {
            conversation_id: conversation.id,
            message_id: msg.id,
            role: msg.role,
            content: msg.content,
            timestamp: msg.timestamp.toISOString()
          });
        });

      const messagesToSave = Array.from(uniqueMessages.values());

      if (messagesToSave.length === 0) {
        console.log('💾 [MESSAGE-SERVICE] No messages to save (only welcome messages or no unique messages)');
        return true; // Nothing to save, but not an error
      }

      console.log(`💾 [MESSAGE-SERVICE] Prepared ${messagesToSave.length} unique messages for saving`);
      console.log(`💾 [MESSAGE-SERVICE] Deduplicated from ${messages.length} original messages`);
      console.log(`💾 [MESSAGE-SERVICE] Messages to save:`, messagesToSave);

      // Use upsert to handle existing messages
      const { data, error } = await supabase
        .from('chat_messages')
        .upsert(messagesToSave, {
          onConflict: 'conversation_id,message_id',
          ignoreDuplicates: false
        })
        .select('id');

      if (error) {
        console.error('❌ [MESSAGE-SERVICE] Error saving messages:', error);
        console.error('❌ [MESSAGE-SERVICE] Error details:', JSON.stringify(error, null, 2));
        return false;
      }

      console.log(`✅ [MESSAGE-SERVICE] Successfully saved ${messagesToSave.length} messages`);
      console.log(`✅ [MESSAGE-SERVICE] DB returned data:`, data);
      console.log(`💾 [MESSAGE-SERVICE] ===== MESSAGE SAVE COMPLETED =====`);
      
      return true;
    } catch (error) {
      console.error('❌ [MESSAGE-SERVICE] Exception saving messages:', error);
      console.error('❌ [MESSAGE-SERVICE] Exception stack:', (error as Error).stack);
      return false;
    }
  }

  /**
   * Load messages from the database for a conversation
   */
  static async loadMessages(threadId: string): Promise<Message[]> {
    try {
      console.log(`📥 [MESSAGE-SERVICE] ===== LOADING MESSAGES =====`);
      console.log(`📥 [MESSAGE-SERVICE] Thread ID: ${threadId}`);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("❌ [MESSAGE-SERVICE] User error during load:", userError);
        return [];
      }
      
      if (!user) {
        console.error("❌ [MESSAGE-SERVICE] No user found for loading");
        return [];
      }

      console.log(`📥 [MESSAGE-SERVICE] User authenticated: ${user.id}`);

      // Get conversation ID from thread_id
      const { data: conversation, error: convError } = await supabase
        .from('chat_conversations')
        .select('id')
        .eq('thread_id', threadId)
        .eq('user_id', user.id)
        .single();

      if (convError || !conversation) {
        console.log('❌ [MESSAGE-SERVICE] Could not find conversation for thread:', threadId);
        console.log('❌ [MESSAGE-SERVICE] Conversation error:', convError);
        return [];
      }

      console.log(`📥 [MESSAGE-SERVICE] Found conversation ID: ${conversation.id}`);

      // Load messages from database
      const { data: dbMessages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('conversation_id', conversation.id)
        .order('timestamp', { ascending: true });

      if (error) {
        console.error('❌ [MESSAGE-SERVICE] Error loading messages:', error);
        return [];
      }

      console.log(`📥 [MESSAGE-SERVICE] Loaded ${dbMessages.length} messages from database`);

      // Convert database messages to Message objects
      const messages: Message[] = dbMessages.map(dbMsg => ({
        id: dbMsg.message_id,
        role: dbMsg.role as 'user' | 'assistant' | 'system',
        content: dbMsg.content,
        timestamp: new Date(dbMsg.timestamp)
      }));

      console.log(`✅ [MESSAGE-SERVICE] Successfully loaded ${messages.length} messages for thread ${threadId}`);
      console.log(`📥 [MESSAGE-SERVICE] ===== LOADING MESSAGES COMPLETED =====`);
      
      return messages;
    } catch (error) {
      console.error('❌ [MESSAGE-SERVICE] Exception loading messages:', error);
      console.error('❌ [MESSAGE-SERVICE] Exception stack:', (error as Error).stack);
      return [];
    }
  }

  /**
   * Delete all messages for a conversation
   */
  static async deleteMessages(threadId: string): Promise<boolean> {
    try {
      console.log(`🗑️ [MESSAGE-SERVICE] ===== DELETING MESSAGES =====`);
      console.log(`🗑️ [MESSAGE-SERVICE] Thread ID: ${threadId}`);
      
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError) {
        console.error("❌ [MESSAGE-SERVICE] User error during delete:", userError);
        return false;
      }
      
      if (!user) {
        console.error("❌ [MESSAGE-SERVICE] No user found for delete");
        return false;
      }

      // Get conversation ID from thread_id
      const { data: conversation, error: convError } = await supabase
        .from('chat_conversations')
        .select('id')
        .eq('thread_id', threadId)
        .eq('user_id', user.id)
        .single();

      if (convError || !conversation) {
        console.log('❌ [MESSAGE-SERVICE] No conversation found for thread:', threadId);
        return true; // Not an error if conversation doesn't exist
      }

      console.log(`🗑️ [MESSAGE-SERVICE] Found conversation ID: ${conversation.id}`);

      // Delete all messages for this conversation
      const { error } = await supabase
        .from('chat_messages')
        .delete()
        .eq('conversation_id', conversation.id);

      if (error) {
        console.error('❌ [MESSAGE-SERVICE] Error deleting messages:', error);
        return false;
      }

      console.log(`✅ [MESSAGE-SERVICE] Successfully deleted messages for thread ${threadId}`);
      console.log(`🗑️ [MESSAGE-SERVICE] ===== DELETE MESSAGES COMPLETED =====`);
      
      return true;
    } catch (error) {
      console.error('❌ [MESSAGE-SERVICE] Exception deleting messages:', error);
      console.error('❌ [MESSAGE-SERVICE] Exception stack:', (error as Error).stack);
      return false;
    }
  }
}
